import React, { createContext, useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { AxiosResponse, AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { UserType } from 'src/accounts/types';

import useInterval from '../common/hooks/useInterval';
import { LocalStorageWrapper, api } from '../common/utils';

import { AuthContextType } from './types';

type AuthProviderProps = {
  children: React.ReactNode;
};

type AuthTokensType = {
  expiresIn: number;
  access: string;
  refresh: string;
};

const ACCESS_TOKEN_EXPIRES_IN_MS = 1500000;

export const AuthContext = createContext<AuthContextType>({
  login: () => {},
  isAuthenticated: () => false,
  isTokenExpired: () => false,
  logout: () => {},
  connectedUser: {} as UserType,
  updateConnectedUser: () => false,
  isLoading: true,
});

const AuthProvider: React.FC<AuthProviderProps> = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [connectedUser, setConnectedUser] = useState<UserType>({} as UserType);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = useCallback(() => {
    const twoWeeks = DateTime.local().plus({ weeks: 2 }).valueOf();
    const authTokens = LocalStorageWrapper.getItem('authTokens');
    return authTokens?.expiresIn && authTokens.expiresIn < twoWeeks;
  }, []);

  const isTokenExpired = useCallback(() => {
    const authTokens = LocalStorageWrapper.getItem('authTokens');
    return authTokens?.refresh && authTokens?.expiresIn < DateTime.local().valueOf();
  }, []);

  const getConnectedUser = useCallback(async () => {
    try {
      const response: AxiosResponse<UserType> = await api.get('/users/current_user');
      setConnectedUser(response.data);
      const user = response.data;
      if (process.env.REACT_APP_ENV !== 'development' && process.env.REACT_APP_ENV !== 'mirage') {
        Sentry.setUser({
          email: user.email,
          id: user.id.toString(),
          username: user.username,
        });
      }
    } catch (error) {
      localStorage.removeItem('authTokens');
      navigate('login');
    }
  }, [navigate]);

  const login = async (username: string, password: string): Promise<number> => {
    try {
      const response: AxiosResponse = await api.post('/api/admin_token', { username, password });
      const tokens = response.data as AuthTokensType;
      tokens.expiresIn = DateTime.local().valueOf() + ACCESS_TOKEN_EXPIRES_IN_MS;
      LocalStorageWrapper.setItem('authTokens', tokens);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getConnectedUser();
      navigate('app/accounts');
      setIsLoading(false);
      return 200;
    } catch (error) {
      const responseError = error as AxiosError;
      LocalStorageWrapper.removeItem('authTokens');
      if (responseError?.response?.status) {
        return responseError.response.status;
      }
      // Otherwise a response was not received, return a bad request
      // code for the caller
      return 400;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('authTokens');
    navigate('login');
  }, [navigate]);

  const getNewTokens: () => Promise<void> = useCallback(async () => {
    const authTokens = LocalStorageWrapper.getItem('authTokens');
    const refreshToken = authTokens ? authTokens.refresh : null;
    try {
      const response: AxiosResponse = await api.post('/api/token/refresh', {
        refresh: refreshToken,
      });
      const tokens = response.data as AuthTokensType;
      tokens.expiresIn = DateTime.local().valueOf() + ACCESS_TOKEN_EXPIRES_IN_MS;
      LocalStorageWrapper.setItem('authTokens', tokens);
    } catch {
      localStorage.removeItem('authTokens');
      navigate('login');
    }
  }, [navigate]);

  const updateConnectedUser = (updatedUser: UserType): void => {
    setConnectedUser((prevUser) => ({
      ...prevUser,
      ...updatedUser,
    }));
  };

  useEffect(() => {
    const fetch = async (): Promise<void> => {
      setIsLoading(true);
      try {
        if (isTokenExpired()) {
          await getNewTokens();
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getConnectedUser();
      } catch (e) {
        LocalStorageWrapper.removeItem('authTokens');
        navigate('login');
      } finally {
        setIsLoading(false);
      }
    };
    if (isAuthenticated()) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch();
    }
  }, [getConnectedUser, getNewTokens, isAuthenticated, isTokenExpired, navigate]);

  useInterval(async () => {
    if (isAuthenticated()) {
      try {
        await getNewTokens();
      } catch {
        navigate('login');
      }
    }
  }, ACCESS_TOKEN_EXPIRES_IN_MS);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isTokenExpired,
        login,
        logout,
        connectedUser,
        updateConnectedUser,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
